import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, User, Phone, Car } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { reportError } from '../../../../lib/errors';

export interface CustomerSearchResult {
    id: string;
    full_name: string;
    phone: string | null;
    email: string | null;
    vehicles?: Array<{
        vrm: string;
        make: string;
        model: string;
        year?: number;
        colour?: string;
        engine?: string;
        fuel_type?: string;
        mileage?: number | null;
    }>;
}

interface CustomerTypeaheadProps {
    onSelect: (customer: CustomerSearchResult) => void;
    placeholder?: string;
    className?: string;
}

export function CustomerTypeahead({ onSelect, placeholder = "Search existing customers...", className = "" }: CustomerTypeaheadProps) {
    const { activeOrgId } = useVertical();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [results, setResults] = useState<CustomerSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search Effect
    useEffect(() => {
        const search = async () => {
            if (!debouncedQuery || debouncedQuery.length < 2) {
                setResults([]);
                return;
            }

            if (!activeOrgId) return;

            setLoading(true);
            try {
                // ---------------------------------------------------------
                // Customer search, in two parts.
                //
                // THIS IS A DELIBERATE BEHAVIOUR CHANGE, and the only one the
                // PII encryption forces on the UI. It used to be one query:
                //
                //   .or(`full_name.ilike.%q%,phone.ilike.%q%,email.ilike.%q%`)
                //
                // Substring matching on email and phone cannot survive
                // encryption. Ciphertext is not searchable by design, and the
                // blind index is a keyed HASH — it answers "is this exactly the
                // stored value?" and nothing else. There is no way to ask it for
                // a partial match without giving up the encryption.
                //
                // So: NAME still matches on substring, and email/phone now match
                // only in full. Typing "900123" no longer finds a customer whose
                // number ends in it; typing the whole number, or the whole email,
                // does — and now case- and format-insensitively, which the old
                // ILIKE only approximated for phone ("07700 900123" did not match
                // a stored "07700900123").
                //
                // Names are the overwhelmingly common way people search here, so
                // the everyday path is unchanged.
                //
                // Also gone: `debouncedQuery` was interpolated straight into a
                // PostgREST .or() filter. A query containing a comma or a
                // parenthesis was parsed as filter SYNTAX rather than as a value,
                // so a customer named "Smith, J" broke the search — and the same
                // hole let a crafted query rewrite the filter expression. The
                // .ilike() builder below escapes its argument properly.
                // ---------------------------------------------------------
                const [byName, exactId] = await Promise.all([
                    supabase
                        .from('customers_decrypted')
                        .select('id, full_name, phone, email')
                        .eq('org_id', activeOrgId)
                        // An archived customer is off the books — deceased,
                        // barred, moved away — so they must not be offered as
                        // someone to book a job for. Restoring them from their
                        // profile puts them back in this list.
                        .is('archived_at', null)
                        .ilike('full_name', `%${debouncedQuery}%`)
                        .limit(5),
                    // Only worth a round trip when the query could plausibly BE a
                    // full contact detail. A two-letter name fragment never is.
                    /[@]/.test(debouncedQuery) || /\d{6,}/.test(debouncedQuery.replace(/\D/g, ''))
                        ? supabase.rpc('fn_find_my_customer_id_by_contact', {
                            p_org_id: activeOrgId,
                            p_email: debouncedQuery.includes('@') ? debouncedQuery : null,
                            p_phone: debouncedQuery.includes('@') ? null : debouncedQuery,
                        })
                        : Promise.resolve({ data: null, error: null }),
                ]);

                if (byName.error) throw byName.error;

                let customers = byName.data ?? [];

                // Fold in the exact contact match, if it is not already present.
                if (exactId?.data && !customers.some(c => c.id === exactId.data)) {
                    const { data: exact, error: exactError } = await supabase
                        .from('customers_decrypted')
                        .select('id, full_name, phone, email')
                        .eq('id', exactId.data)
                        .is('archived_at', null)
                        .maybeSingle();
                    if (exactError) reportError(exactError, 'customer-typeahead.customers-decrypted');
                    if (exact) customers = [exact, ...customers].slice(0, 5);
                }

                if (!customers || customers.length === 0) {
                    setResults([]);
                    return;
                }

                // For the found customers, fetch their vehicles via the jobs table
                const customerIds = customers.map(c => c.id);
                const { data: jobLinks, error: vError } = await supabase
                    .from('jobs')
                    .select('customer_id, vehicles(vrm, make, model, year, colour, engine, fuel_type, mileage)')
                    .eq('org_id', activeOrgId)
                    .in('customer_id', customerIds);

                if (vError) throw vError;

                const enrichedCustomers: CustomerSearchResult[] = customers.map(c => {
                    const links = jobLinks?.filter(jl => jl.customer_id === c.id) || [];
                    const allVehicles = links.map(l => l.vehicles as any).filter(Boolean);
                    // Deduplicate vehicles by VRM
                    const vehiclesMap = new Map();
                    for (const v of allVehicles) {
                        if (!vehiclesMap.has(v.vrm)) {
                            vehiclesMap.set(v.vrm, v);
                        }
                    }
                    return {
                        ...c,
                        vehicles: Array.from(vehiclesMap.values())
                    };
                });

                setResults(enrichedCustomers);
                setIsOpen(true);
            } catch (err) {
                console.error('Customer search failed:', err);
            } finally {
                setLoading(false);
            }
        };

        search();
    }, [debouncedQuery, activeOrgId]);

    const handleSelect = (customer: CustomerSearchResult) => {
        setQuery('');
        setIsOpen(false);
        onSelect(customer);
    };

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-4" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => { if (results.length > 0) setIsOpen(true); }}
                    placeholder={placeholder}
                    className="w-full bg-tint/5 border border-tint/10 rounded-lg pl-9 pr-10 py-2.5 text-fg text-sm focus:ring-2 focus:ring-accent outline-none"
                    autoComplete="off"
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent animate-spin" />
                )}
            </div>

            {/* Results Dropdown */}
            {isOpen && query.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-raised border border-line rounded-lg shadow-xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 max-h-60 overflow-y-auto">
                    {results.length > 0 ? (
                        <div className="py-1">
                            {results.map((result) => (
                                <button
                                    key={result.id}
                                    type="button"
                                    onClick={() => handleSelect(result)}
                                    className="w-full text-left px-3 py-2 hover:bg-tint/5 flex flex-col gap-1 transition-colors border-b border-tint/5 last:border-0"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-info/10 text-info p-1 rounded-md">
                                                <User className="w-3.5 h-3.5" />
                                            </div>
                                            <span className="text-fg font-medium text-sm">{result.full_name}</span>
                                        </div>
                                        {result.phone && (
                                            <div className="flex items-center gap-1 text-fg-4 text-xs">
                                                <Phone className="w-3 h-3" />
                                                <span>{result.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {result.vehicles && result.vehicles.length > 0 && (
                                        <div className="pl-8 pt-1 flex flex-wrap gap-1">
                                            {result.vehicles.map(v => (
                                                <span key={v.vrm} className="inline-flex items-center gap-1 bg-tint/5 text-fg-3 px-1.5 py-0.5 rounded text-[10px] border border-tint/10">
                                                    <Car className="w-2.5 h-2.5" />
                                                    <span className="font-mono">{v.vrm}</span>
                                                    <span>• {v.make} {v.model}</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    ) : !loading && (
                        <div className="p-4 text-center text-sm text-fg-4">
                            No matching customers found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
